// PRD-01 US-AUTH-06.

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { authStore } from "@/lib/auth";

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  // Zie LoginPage: geen "demo" als beginwaarde voor iemand die hier voor het
  // eerst komt.
  const [tenantSlug, setTenantSlug] = useState(authStore.getTenantSlug() ?? "");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErrorKey(null);
    try {
      await api.post("/v1/auth/forgot-password", { email, tenantSlug }, { skipAuth: true });
      setSubmitted(true);
    } catch (err) {
      // Met anti-enumeration zou dit zelden falen, maar net for safety
      setErrorKey(err instanceof ApiError ? err.i18nKey : "errors.internal");
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4 py-12">
        <div className="max-w-md bg-white rounded-2xl shadow-sm border border-neutral-200 p-8 text-center">
          <h1 className="text-xl font-semibold mb-2">
            {t("auth.forgot.checkInbox", "Check je inbox")}
          </h1>
          <p className="text-sm text-neutral-700 mb-4">
            {t(
              "auth.forgot.checkBody",
              "Als dit e-mailadres bij ons bekend is, hebben we een resetlink gestuurd. De link is 1 uur geldig."
            )}
          </p>
          <Link to="/login" className="text-brand-600 hover:text-brand-700 text-sm font-medium">
            {t("common.back", "Terug naar inloggen")}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-brand-50 to-white">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-neutral-200 p-8">
        <h1 className="text-2xl font-semibold mb-1">
          {t("auth.forgot.title", "Wachtwoord vergeten?")}
        </h1>
        <p className="text-sm text-neutral-600 mb-6">
          {t(
            "auth.forgot.subtitle",
            "Vul je e-mail in. We sturen je een link om je wachtwoord opnieuw in te stellen."
          )}
        </p>

        <form onSubmit={onSubmit} className="space-y-4" aria-busy={loading}>
          <div>
            <label htmlFor="tenantSlug" className="block text-sm font-medium text-neutral-700 mb-1">
              {t("auth.login.tenant", "Organisatie")}
            </label>
            <input
              id="tenantSlug"
              type="text"
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
              required
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              placeholder={t("auth.login.tenantPlaceholder", "bijv. amsterdam")}
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
              {t("auth.login.email", "E-mail")}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="username"
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
            />
          </div>

          {errorKey && (
            <div role="alert" className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
              {t(errorKey)}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-medium rounded-lg"
          >
            {loading
              ? t("auth.forgot.sending", "Versturen…")
              : t("auth.forgot.submit", "Stuur resetlink")}
          </button>
        </form>

        <p className="mt-4 text-center text-sm">
          <Link to="/login" className="text-neutral-600 hover:text-neutral-900">
            {t("common.back", "Terug")}
          </Link>
        </p>
      </div>
    </main>
  );
}
